import getConfig from 'next/config';

import {getApiRequest, postApiRequest, patchApiRequest, deleteApiRequest} from '~/src/utilities/feed/api';
import {getRandomKey} from '~/src/utilities/helpers/string';
import {getData, setData} from '~/src/utilities/helpers/context';
import {Logger} from '~/src/utilities/helpers/log';

const {publicRuntimeConfig: config} = getConfig();

const filterReactions = (filter) => getData().reactions.filter((r) => r.id !== filter.id);

/**
 * check if a comment has a certain reaction by the user
 *
 * @param {string} commentId id of a comment
 * @param {string} reaction key of a comment reaction
 */
const hasReaction = (commentId, reaction) => {
    const reactions = getData().reactions;
    return reactions.some((c) => c.commentId === commentId && (!reaction || c.reaction === reaction) && c.deleted !== true);
};

/**
 * fetch possible comment reactions
 */
const getReactionConfig = async () => {
    try {
        const {collection} = await getApiRequest(`${config.feeds.reactions}?tenantId=${config.tenantId}`);

        return collection;
    } catch (error) {
        Logger.info(`[${error.name}][Rating] Error while getting reaction configuration`, {error});
    }

    return null;
};

/**
 * create a new comment reaction
 *
 * @param {string} commentId id of a comment
 * @param {string} reaction key of a comment reaction
 */
const createReaction = async (commentId, reaction) => {
    const transientReaction = {
        transient: true,
        id: getRandomKey(),
        commentId,
        reaction
    };

    setData({reactions: [...getData().reactions, transientReaction]});

    try {
        const {reactionId} = await postApiRequest(config.feeds.reactions, {commentId, reactionKey: reaction});

        const reactionData = {
            id: reactionId,
            commentId,
            reaction
        };

        setData({reactions: [...filterReactions(transientReaction), reactionData]});
    } catch (error) {
        Logger.info(`[${error.name}][Rating] Error while creating reactoin`, {error});

        setData({reactions: filterReactions(transientReaction)});
    }
};

/**
 * update an existing comment reaction
 *
 * @param {object} existingReaction object representing an existing reaction
 * @param {string} reaction key of a comment reaction
 */
const updateReaction = async (existingReaction, reaction) => {
    const transientReaction = {
        ...existingReaction,
        transient: true,
        reaction
    };

    setData({reactions: [...filterReactions(existingReaction), transientReaction]});

    try {
        await patchApiRequest(`${config.feeds.reactions}/${existingReaction.id}`, {reactionKey: reaction});

        const reactionData = {
            ...existingReaction,
            reaction
        };

        setData({reactions: [...filterReactions(transientReaction), reactionData]});
    } catch (error) {
        Logger.info(`[${error.name}][Rating] Error while updating reaction`, {error});

        setData({reactions: [...filterReactions(transientReaction), existingReaction]});
    }
};

/**
 * remove an existing comment reaction
 *
 * @param {object} existingReaction object representing an existing reaction
 */
const deleteReaction = async (existingReaction) => {
    const transientReaction = {
        ...existingReaction,
        transient: true,
        deleted: true
    };

    setData({reactions: [...filterReactions(existingReaction), transientReaction]});

    try {
        await deleteApiRequest(`${config.feeds.reactions}/${existingReaction.id}`);

        setData({reactions: filterReactions(transientReaction)});
    } catch (error) {
        Logger.info(`[${error.name}][Rating] Error while deleting reactoin`, {error});

        setData({reactions: [...filterReactions(transientReaction), existingReaction]});
    }
};

/**
 *
 * @param {string} commentId id of a comment
 * @param {string} reaction key of a comment reaction
 */
const reactToComment = async (commentId, reaction) => {
    const existingReaction = getData().reactions.find((c) => c.commentId === commentId);

    if (!existingReaction) {
        createReaction(commentId, reaction);
    } else {
        if (!existingReaction.transient) {
            if (existingReaction.reaction === reaction) {
                deleteReaction(existingReaction);
            } else {
                updateReaction(existingReaction, reaction);
            }
        }
    }
};

/**
 * Possible action types for the reaction context reducer
 */
const ActionTypes = {
    NETWORK_SUCCESS: 0,
    NETWORK_FAILURE: 1,
    UPDATE_CONFIG: 2
};

/**
 * reaction context reduer
 *
 * @param {object} state current state of the reducer
 * @param {object} action configuration for manipulating the state of the reducer
 */
const reducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.NETWORK_SUCCESS:
            return state;
        case ActionTypes.NETWORK_FAILURE:
            return state;
        case ActionTypes.UPDATE_CONFIG:
            return {
                ...state,
                config: action.data || []
            };
        default:
            return state;
    }
};

export {hasReaction, getReactionConfig, reactToComment, ActionTypes, reducer};
